/* global SideDrawer */

if (!customElements.get('quick-add-drawer')) {
  class QuickAddDrawer extends SideDrawer {
    constructor() {
      super();
      this.content = this.querySelector('.js-product-details');
      this.footer = this.querySelector('.drawer__footer');
      this.footerContent = this.querySelector('.js-footer-content');
      this.notification = this.querySelector('.js-added-to-cart');
      this.backBtn = this.querySelector('.drawer__back-btn');
      this.openCartDrawerLinks = this.querySelectorAll('.js-open-cart-drawer');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.fetch = null;
      this.fetchedUrls = [];
      this.defaultInventoryData = null;
      this.quickAddButtonMouseEnterHandler = this.handleQuickAddButtonMouseEnter.bind(this);
      this.documentClickHandler = this.handleDocumentClick.bind(this);

      document.addEventListener('click', this.documentClickHandler);
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);

      this.addEventListener('on:variant:change:early', this.boundHandleVariantChange);
      this.addEventListener('on:variant:change', this.boundHandleVariantChange);

      this.openCartDrawerLinks.forEach((link) => {
        link.addEventListener('click', this.handleOpenCartClick.bind(this));
      });

      if (theme.device.hasHover && theme.mediaMatches.md) {
        document.querySelectorAll('.js-quick-add').forEach((button) => {
          this.bindQuickAddButtonMouseEnter(button);
        });

        if ('MutationObserver' in window) {
          this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    this.bindQuickAddButtonMouseEnter(button);
                  });
                }
              });

              mutation.removedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
                  });
                }
              });
            });
          });

          this.observer.observe(document.body, { childList: true, subtree: true });
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.documentClickHandler);
      document.querySelectorAll('.js-quick-add').forEach((button) => {
        button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      });
      if (this.observer) this.observer.disconnect();
    }

    bindQuickAddButtonMouseEnter(button) {
      if (!button.dataset.quickAddListenerAdded) {
        button.dataset.quickAddListenerAdded = 'true';
        button.addEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      }
    }

    handleQuickAddButtonMouseEnter(evt) {
      if (!this.fetchedUrls.includes(evt.target.dataset.productUrl)) {
        this.fetch = {
          url: evt.target.dataset.productUrl,
          promise: fetch(evt.target.dataset.productUrl)
        };
        this.fetchedUrls.push(evt.target.dataset.productUrl);
      }
    }

    handleOpenCartClick(evt) {
      if (this.cartDrawer) {
        evt.preventDefault();
        this.cartDrawer.open();
      } else if (window.location.pathname === theme.routes.cart) {
        evt.preventDefault();
        this.close();
      }
    }

    handleDocumentClick(evt) {
      if (!evt.target.matches('.js-quick-add')) return;

      if (this.cartDrawer && this.cartDrawer.ariaHidden === 'false') {
        const overlay = document.querySelector('.js-overlay.is-visible');
        if (overlay) overlay.style.transitionDelay = '200ms';

        this.cartDrawer.close();

        setTimeout(() => {
          this.backBtn.hidden = false;
          this.open(evt.target);
          if (overlay) overlay.style.transitionDelay = '';
        }, 200);
      } else {
        this.open(evt.target);
      }
    }

    getInventoryFromProductEl() {
      if (!this.productEl) return null;

      const dataEl = this.productEl.querySelector('.js-inventory-data');
      if (!dataEl) return null;

      try {
        return JSON.parse(dataEl.textContent);
      } catch (e) {
        return null;
      }
    }

    applyQuickAddQuantityLimits(inventory = null) {
      const inventoryData = inventory || this.defaultInventoryData;
      if (!inventoryData) return;

      this.querySelectorAll('quantity-input').forEach((qtyEl) => {
        const input = qtyEl.querySelector('.qty-input__input');
        if (!input) return;

        const min = input.min !== '' ? Number(input.min) : 1;

        if (inventoryData.inventory_management && inventoryData.inventory_policy !== 'continue') {
          if (inventoryData.inventory_quantity > 0) {
            input.max = inventoryData.inventory_quantity;
          } else {
            input.max = 0;
          }
        } else {
          input.removeAttribute('max');
        }

        let value = Number(input.value);
        if (Number.isNaN(value)) value = min;
        if (value < min) value = min;

        const max = input.max !== '' ? Number(input.max) : null;
        if (max !== null && value > max) value = max;

        input.value = String(value);

        if (typeof qtyEl.syncButtons === 'function') {
          qtyEl.syncButtons();
        }
      });
    }

    updateQuickAddAddToCartButton(variant) {
      const form = this.querySelector('.js-product-form');
      if (!form) return;

      const addBtn = form.querySelector('[name="add"]');
      if (!addBtn) return;

      // No variant
      if (!variant) {
        addBtn.disabled = true;
        addBtn.textContent = theme.strings.noVariant;
        return;
      }

      // Sold out
      if (!variant.available) {
        addBtn.disabled = true;
        addBtn.textContent = theme.strings.noStock;
        return;
      }

      // Available
      addBtn.disabled = false;
      addBtn.textContent = addBtn.dataset.addToCartText || theme.strings.addToCart || 'Add to cart';
    }

    handleVariantChange(evt) {
      const variant = evt.detail?.variant;
      if (!variant) return;

      let url = this.productUrl;
      const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
      url += `${separator}variant=${variant.id}`;

      this.querySelectorAll('.js-prod-link').forEach((link) => {
        link.href = url;
      });

      this.updateQuickAddInventoryUI(variant);

      if (evt.type === 'on:variant:change:early') {
        this.updateQuickAddAddToCartButton(variant);

        const isSoldOut = variant.available === false && variant.inventory_policy !== 'continue';

        if (isSoldOut) {
          this.querySelectorAll('quantity-input').forEach((qtyEl) => {
            const input = qtyEl.querySelector('.qty-input__input');
            const minus = qtyEl.querySelector('.btn--minus');
            const plus = qtyEl.querySelector('.btn--plus');

            if (input) {
              input.value = '0';
              input.max = 0;
            }

            if (typeof qtyEl.setButtonState === 'function') {
              qtyEl.setButtonState(minus, true);
              qtyEl.setButtonState(plus, true);
            }
          });
        } else {
          this.applyQuickAddQuantityLimits({
            inventory_quantity: typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity : 0,
            inventory_policy: variant.inventory_policy,
            inventory_management: variant.inventory_management
          });
        }

        return;
      }

      if (evt.type === 'on:variant:change' && variant.featured_media) {
        this.updateMedia(variant.featured_media);
      }
    }

    updateQuickAddInventoryUI(variant) {
      const root = this.querySelector('.js-product-details');
      if (!root || !variant) return;

      const count = typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity : 0;
      const available = !!variant.available;
      const policy = variant.inventory_policy || 'deny';

      let level = 'none';
      if (count <= 0) {
        if (policy === 'continue') {
          level = 'backordered';
        } else if (available) {
          level = 'in_stock';
        } else {
          level = 'none';
        }
      } else if (count === 1) {
        level = 'very_low';
      } else if (count <= 2) {
        level = 'low';
      } else {
        level = 'normal';
      }

      let text = '';
      if (level === 'backordered') {
        text = theme.strings.backordered;
      } else if (level === 'very_low') {
        text = theme.strings.onlyXLeft.replace('[quantity]', count);
      } else if (level === 'low') {
        text = theme.strings.inStock.replace('[quantity]', count);
      } else if (level === 'normal' || level === 'in_stock') {
        text = count > 0
          ? theme.strings.inStock.replace('[quantity]', count)
          : theme.strings.inStock.replace('[quantity]', 0);
      } else {
        text = theme.strings.noStock;
      }

      const inventoryComponent = root.querySelector('product-inventory');
      if (inventoryComponent) {
        inventoryComponent.hidden = false;
        inventoryComponent.setAttribute('data-inventory-level', level);

        const notice = inventoryComponent.querySelector('.js-inventory-notice');
        if (notice) {
          notice.innerText = text;
        }

        const urgency = inventoryComponent.querySelector('.js-inventory-urgency');
        if (urgency) {
          if (level === 'very_low') {
            urgency.innerHTML = inventoryComponent.dataset.textVeryLow || '';
          } else if (level === 'low') {
            urgency.innerHTML = inventoryComponent.dataset.textLow || '';
          } else if (level === 'backordered') {
            urgency.innerHTML = inventoryComponent.dataset.textNoStockBackordered || '';
          } else if (level === 'normal' || level === 'in_stock') {
            urgency.innerHTML = inventoryComponent.dataset.textNormal || '';
          } else {
            urgency.innerHTML = inventoryComponent.dataset.textNoStock || '';
          }
        }

        return;
      }

      const statusEl = root.querySelector('.product-inventory__status, .quick-add-inventory, [data-inventory-level]');
      if (statusEl) {
        statusEl.hidden = false;
        statusEl.setAttribute('data-inventory-level', level);
        statusEl.textContent = text;
      }
    }

    async open(opener) {
      opener.setAttribute('aria-disabled', 'true');

      if (this.notification) this.notification.hidden = true;

      if (this.productUrl && this.productUrl === opener.dataset.productUrl) {
        super.open(opener);

        this.applyQuickAddQuantityLimits();

        if (opener.dataset.selectedColor) {
          this.setActiveVariant(opener);
        }

        opener.removeAttribute('aria-disabled');
        return;
      }

      this.productUrl = opener.dataset.productUrl;
      this.defaultInventoryData = null;
      this.content.innerHTML = '';
      this.classList.add('is-loading');
      this.content.classList.add('drawer__content--out');
      this.footer.classList.add('drawer__footer--out');

      super.open(opener);

      if (!this.fetch || this.fetch.url !== opener.dataset.productUrl) {
        this.fetch = {
          url: opener.dataset.productUrl,
          promise: fetch(opener.dataset.productUrl)
        };
      }

      const response = await this.fetch.promise;
      if (response.ok) {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        this.productEl = tmpl.content.getElementById('quick-buy-template').content.querySelector('.js-product');
        this.defaultInventoryData = this.getInventoryFromProductEl();
        this.renderProduct(opener);
      }

      this.fetch = null;
      opener.removeAttribute('aria-disabled');
    }

    close() {
      super.close(() => {
        this.backBtn.hidden = true;
      });
    }

    renderProduct(opener) {
      QuickAddDrawer.convertToQuickBuyContent(this.productEl, this.productUrl);

      this.updateFooter();
      this.updateContent();

      // Initialise qty limits/button state for the currently selected variant
      setTimeout(() => {
        const variantPicker = this.querySelector('variant-picker');
        const currentVariant =
          variantPicker?.currentVariant ||
          variantPicker?.variant ||
          null;

        if (currentVariant) {
          this.updateQuickAddAddToCartButton(currentVariant);
          this.updateQuickAddInventoryUI(currentVariant);

          const isSoldOut =
            currentVariant.available === false &&
            currentVariant.inventory_policy !== 'continue';

          if (isSoldOut) {
            this.querySelectorAll('quantity-input').forEach((qtyEl) => {
              const input = qtyEl.querySelector('.qty-input__input');
              const minus = qtyEl.querySelector('.btn--minus');
              const plus = qtyEl.querySelector('.btn--plus');

              if (input) {
                input.value = '0';
                input.max = 0;
              }

              if (typeof qtyEl.setButtonState === 'function') {
                qtyEl.setButtonState(minus, true);
                qtyEl.setButtonState(plus, true);
              } else if (typeof qtyEl.syncButtons === 'function') {
                qtyEl.syncButtons();
              }
            });
          } else {
            this.applyQuickAddQuantityLimits({
              inventory_quantity:
                typeof currentVariant.inventory_quantity === 'number'
                  ? currentVariant.inventory_quantity
                  : 0,
              inventory_policy: currentVariant.inventory_policy,
              inventory_management: currentVariant.inventory_management
            });
          }
        } else if (this.defaultInventoryData) {
          this.applyQuickAddQuantityLimits(this.defaultInventoryData);

          this.updateQuickAddInventoryUI({
            available: this.defaultInventoryData.available,
            inventory_quantity: this.defaultInventoryData.inventory_quantity,
            inventory_policy: this.defaultInventoryData.inventory_policy,
            inventory_management: this.defaultInventoryData.inventory_management
          });
        }

        // Keep the theme's normal announcement behaviour
        if (variantPicker && typeof variantPicker.announceChange === 'function') {
          variantPicker.announceChange();
        }
      }, 20);

      if (opener.dataset.selectedColor) {
        setTimeout(this.setActiveVariant.bind(this, opener), 10);
      }

      window.initLazyImages();
    }

    setActiveVariant(opener) {
      const colorOptionBox = this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`);
      if (colorOptionBox) {
        this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`).click();
      } else {
        const colorOptionDropdown = this.querySelector(
          `.custom-select__option[data-value="${opener.dataset.selectedColor}"]`
        );
        if (colorOptionDropdown) {
          const customSelect = colorOptionDropdown.closest('custom-select');
          customSelect.selectOption(colorOptionDropdown);
        }
      }
    }

    updateMedia(media) {
      if (!media) return;

      const { src } = media.preview_image;
      const container = this.querySelector('.quick-add-info__media');
      const width = container.offsetWidth;
      const aspectRatio = media.preview_image.aspect_ratio;

      const existingId = container.dataset.mediaId;
      const existingImg = container.querySelector('img');

      if (!existingImg || existingId !== `${media.id}`) {
        container.innerHTML = `
          <img src="${src}&width=${width}" srcset="${src}&width=${width}, ${src}&width=${width * 2} 2x" width="${width * 2}" height="${(width * 2) / aspectRatio}" alt="${media.alt || ''}">
        `;
        container.dataset.mediaId = media.id;
      }
    }

    updateContent() {
      const productContent = this.productEl.querySelector('.js-product-details');
      if (productContent) {
        this.content.innerHTML = productContent.innerHTML;
      }

      this.classList.remove('is-loading');
      this.content.classList.remove('drawer__content--out');
    }

    updateFooter() {
      const footerContent = this.productEl.querySelector('.js-footer-content');
      this.footer.classList.remove('quick-add__footer-message');

      if (footerContent && footerContent.hasChildNodes()) {
        this.footerContent.innerHTML = footerContent.innerHTML;
      } else {
        this.footer.classList.add('quick-add__footer-message');
        this.footerContent.innerHTML = `
          <div class="alert quick-add__alert bg-info-bg text-info-text">
            <div class="flex">
              <div>
                <svg class="icon icon--price_tag" width="32" height="32" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                  <path fill="currentColor" d="M7.59 1.34a1 1 0 01.7-.29h5.66a1 1 0 011 1v5.66a1 1 0 01-.3.7L7.6 15.5a1 1 0 01-1.42 0L.52 9.83a1 1 0 010-1.42l7.07-7.07zm6.36 6.37l-7.07 7.07-5.66-5.66L8.3 2.05h5.66v5.66z" fill-rule="evenodd"/>
                  <path fill="currentColor" d="M9.7 6.3a1 1 0 101.42-1.42 1 1 0 00-1.41 1.41zM9 7a2 2 0 102.83-2.83A2 2 0 009 7z" fill-rule="evenodd"/>
                </svg>
              </div>
              <div>
                <p class="text-h6 font-bold mb-2">${theme.strings.awaitingSale}</p>
                <a class="link js-prod-link" href="${this.productUrl}">${theme.strings.viewDetails}</a>
              </div>
            </div>
          </div>`;
      }

      this.footer.classList.remove('drawer__footer--out');
    }

    addedToCart() {
      if (this.notification) {
        this.notification.hidden = false;

        setTimeout(() => {
          this.notification.hidden = true;
        }, this.notification.dataset.visibleFor);
      }
    }

    static convertToQuickBuyContent(container) {
      const variantPicker = container.querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.dataset.updateUrl = 'false';
      }

      const sizeChartModal = container.querySelector('[data-modal="size-chart"]');
      if (sizeChartModal) {
        sizeChartModal.remove();
      }
    }
  }

  customElements.define('quick-add-drawer', QuickAddDrawer);

  window.theme = window.theme || {};
  window.theme.quickBuy = window.theme.quickBuy || {};
  window.theme.quickBuy.convertToQuickBuyContent = QuickAddDrawer.convertToQuickBuyContent;
}